/**
 * Invoice detail popup — the whole commercial picture of one IPT invoice
 * without leaving the contract, plus a deep link into Invoice & Payment Tracking.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowUpRight, Loader2, Link2, Link2Off, AlertOctagon } from 'lucide-react';
import api from '@/lib/api';
import { fmtINR, fmtDate } from './portfolioUi';

const STAGES = ['RECEIVED', 'CERTIFIED', 'SEN_GRN_POSTED', 'VERIFIED', 'PRE_AUDITED', 'APPROVED', 'PAID'];

const Cell = ({ label, value, tone }) => (
  <div className="py-3 px-4 border-b border-r" style={{ borderColor: 'var(--border-subtle)' }}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>{label}</p>
    <p className="text-sm mt-1 tabular-nums" style={{ color: tone || 'var(--text-primary)' }}>{value ?? '—'}</p>
  </div>
);

export const InvoiceDetailDialog = ({ open, onOpenChange, invoice, contractId, linked, onLinkChanged }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const id = invoice?.invoice_id;

  useEffect(() => {
    if (!open || !id) { setDetail(null); return; }
    setLoading(true);
    api.get(`/ipt/invoices/${id}`)
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [open, id]);

  if (!invoice) return null;
  const inv = detail?.invoice || invoice;
  const stageIndex = STAGES.indexOf(String(inv.current_stage || '').toUpperCase());
  const payable = detail?.payable || {};
  const deductions = detail?.deductions || [];

  const toggleLink = async () => {
    setBusy(true);
    try {
      if (linked) await api.delete(`/contracts/contracts/${contractId}/invoice-links/${id}`);
      else await api.post(`/contracts/contracts/${contractId}/invoice-links`, { invoice_ids: [id], mode: 'manual' });
      onLinkChanged?.();
      onOpenChange(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden" data-testid="invoice-detail-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
          <DialogTitle className="text-xl tracking-tight flex items-center gap-3">
            {inv.invoice_no}
            {inv.is_blocked && (
              <span className="inline-flex items-center gap-1 text-[11px] rounded-full border px-2 py-0.5"
                    style={{ color: '#B91C1C', borderColor: '#B91C1C55' }}>
                <AlertOctagon className="w-3 h-3" /> blocked
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {inv.vendor_name} · vendor ref {inv.vendor_invoice_no || '—'} · contract ref {inv.contract_reference || '—'}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[66vh] overflow-y-auto">
          {/* stage tracker */}
          <div className="px-6 py-5">
            <div className="flex items-center gap-1">
              {STAGES.map((s, i) => (
                <div key={s} className="flex-1" data-testid={`stage-${s}`}>
                  <span className="block h-1.5 rounded-full"
                        style={{ background: i <= stageIndex ? 'var(--brand-accent)' : 'var(--surface-bg)' }} />
                  <span className="block text-[9px] mt-1.5 uppercase tracking-wider truncate"
                        style={{ color: i === stageIndex ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {s.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {loading && (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--brand-accent)' }} /></div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
            <Cell label="Gross" value={fmtINR(inv.gross_amount)} />
            <Cell label="Net payable" value={fmtINR(inv.net_payable)} />
            <Cell label="Currency" value={inv.currency} />
            <Cell label="Invoice date" value={fmtDate(inv.invoice_date)} />
            <Cell label="Received" value={fmtDate(inv.receipt_date)} />
            <Cell label="Due" value={fmtDate(inv.due_date)} tone={inv.due_date && new Date(inv.due_date) < new Date() ? '#B91C1C' : undefined} />
            <Cell label="Terms" value={inv.payment_terms_days ? `${inv.payment_terms_days} days` : '—'} />
            <Cell label="Type" value={inv.invoice_type} />
            <Cell label="Project" value={inv.project_id} />
            {payable.totalDeductions != null && <Cell label="Deductions" value={fmtINR(payable.totalDeductions)} />}
            {payable.payableNow != null && <Cell label="Payable now" value={fmtINR(payable.payableNow)} />}
            <Cell label="Match basis" value={invoice.reason || (linked ? 'Linked' : '—')} />
          </div>

          {inv.is_blocked && inv.block_reason && (
            <div className="px-6 py-4 text-sm" style={{ color: '#B91C1C' }} data-testid="invoice-block-reason">
              Block reason: {inv.block_reason}
            </div>
          )}

          {deductions.length > 0 && (
            <div className="px-6 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--text-muted)' }}>Deductions</p>
              {deductions.slice(0, 8).map((d, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                  <span style={{ color: 'var(--text-secondary)' }}>{d.deduction_type || d.type}</span>
                  <span className="tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmtINR(d.amount ?? d.deduction_amount ?? d.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
          <button onClick={toggleLink} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs"
                  style={{ borderColor: 'var(--border-color)', color: linked ? '#B91C1C' : 'var(--text-primary)' }}
                  data-testid="invoice-dialog-link-toggle">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : linked ? <Link2Off className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {linked ? 'Unlink from contract' : 'Link to this contract'}
          </button>
          <button onClick={() => navigate(`/project-controls/cost/invoices/${id}`)}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs text-white"
                  style={{ background: 'var(--brand-accent)' }}
                  data-testid="invoice-dialog-open-ipt">
            Open in Invoice &amp; Payment Tracking <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceDetailDialog;
